import {useTranslation} from 'react-i18next';

import {isBeforeToday, workdayLabelMap} from '@lib/logic';
import {isOfficeWorkday} from '@lib/store/src/data/timeslots/utils';
import {selectBuilding, selectTimeslot, useAppDispatch} from '@lib/store';
import {Workday} from '@lib/store/src/data/timeslots/apiTypes';
import {WorkdayHeaderTitle} from './WorkdayTileHeaderTitle';
import {dateKeyForDateTimeString, timeLabel} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {useModal} from '@hooks';
import {WorkdayDetailsModal} from '../WorkdayDetailsModal';
import {cn} from '@utils';
import {WorkdayIcon} from '@molecules';

interface WorkdayTileHeaderProps {
  selectedDate: string;
  workday?: Workday;
}

export const WorkdayTileHeader = ({selectedDate, workday}: WorkdayTileHeaderProps) => {
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const {setModalPages, openModal} = useModal();
  const dateKey = dateKeyForDateTimeString(selectedDate);

  const isPast = isBeforeToday(selectedDate);

  const handleClick = () => {
    if (isPast || !workday) return;

    if (isOfficeWorkday(workday)) {
      dispatch(selectBuilding(workday.buildingId));
    }

    dispatch(
      selectTimeslot({
        startTime: workday.localStart,
        endTime: workday.localEnd,
      }),
    );

    setModalPages([
      <WorkdayDetailsModal
        selectedDate={dateKey}
        workday={workday}
      />,
    ]);

    openModal();
  };

  return (
    <button
      type="button"
      disabled={isPast}
      onClick={handleClick}
      className={cn('flex items-center justify-between p-1.5 gap-4 w-full', {
        'cursor-default opacity-60': isPast,
        'cursor-default': !workday,
      })}>
      <WorkdayIcon
        status={workday?.status}
        className="!bg-energizing-yellow-500*"
      />

      <div className="flex flex-col justify-center flex-1 gap-[2px]">
        {workday ? (
          isOfficeWorkday(workday) ? (
            <WorkdayHeaderTitle
              title={workday.buildingName}
              subtitle={timeLabel(workday.localStart, workday.localEnd, t)}
            />
          ) : (
            <WorkdayHeaderTitle
              title={t(workdayLabelMap[workday.status])}
              subtitle={timeLabel(workday.localStart, workday.localEnd, t)}
            />
          )
        ) : (
          <WorkdayHeaderTitle
            title={t('workplace:WhereWillYouBe')}
            titleSize="large"
          />
        )}
      </div>
    </button>
  );
};
